<template>
  <div>
    <send-modal :visible="visibleSendModal" @close="visibleSendModal = false" @send="send" />

    <a-popover :title="null" placement="bottom" :style="buttonStyle">
      <div class="flex flex-column gap-2" slot="content">
        <a-button
          :type="type"
          icon="mail"
          @click="openSendModal"
          :disabled="disabled.send || loading"
          :loading="loadingSend"
        >
          Send to Email
        </a-button>
        <a-tooltip v-if="resolvedHintText" placement="right">
          <template slot="title">{{ resolvedHintText }}</template>
          <a-button
            :type="type"
            icon="download"
            @click="download"
            :disabled="disabled.download || loading"
            :loading="loadingDownload"
          >
            Download
          </a-button>
        </a-tooltip>
        <a-button
          v-else
          :type="type"
          icon="download"
          @click="download"
          :disabled="disabled.download || loading"
          :loading="loadingDownload"
        >
          Download
        </a-button>
      </div>
      <a-button :size="onlyIcon ? 'small' : 'default'" :title="label" :loading="loading" icon="export" :type="type">{{
        onlyIcon ? '' : label
      }}</a-button>
    </a-popover>
  </div>
</template>

<script>
import { Button, message, notification, Popover, Tooltip } from 'ant-design-vue'
import SendModal from './send-modal.vue'
import { deepCopy } from '@/utils/deep-copy'
import { createNamespacedHelpers } from 'vuex'
import util from '@/util'

const { mapActions: documentActions } = createNamespacedHelpers('document')

export default {
  components: {
    'a-popover': Popover,
    'a-tooltip': Tooltip,
    'a-button': Button,
    'send-modal': SendModal
  },
  props: {
    id: {
      type: Number,
      default: undefined
    },
    exportPayload: {
      type: Object,
      default: undefined
    },
    buttonStyle: {
      type: Object,
      default: () => {}
    },
    disabled: {
      type: Object,
      default: () => ({
        send: false,
        download: false
      })
    },
    loading: {
      type: Boolean,
      default: false
    },
    onlyIcon: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'primary'
    },
    hint: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Export'
    }
  },
  emits: ['download', 'send'],
  data() {
    return {
      visibleSendModal: false,

      loadingDownload: false,
      loadingSend: false
    }
  },
  computed: {
    resolvedHintText() {
      const trimmedHint = this.hint.trim()

      return trimmedHint || null
    }
  },
  methods: {
    ...documentActions(['exportDocument']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    openSendModal() {
      this.visibleSendModal = true
    },

    async download() {
      if (!this.exportPayload) {
        this.$emit('download', this.id)

        return
      }

      const payload = {
        export_type: 'download',
        ...deepCopy(this.exportPayload)
      }

      const loadingMessage = message.loading('Loading..', 0)

      this.loadingDownload = true

      try {
        const { file_url } = await this.exportDocument(payload)

        util.downloadFile(file_url)
        this.$emit('updateUrl', file_url)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        if (loadingMessage()) {
          loadingMessage().destroy()
        }

        this.loadingDownload = false
      }
    },

    async send(email) {
      this.visibleSendModal = false

      if (!this.exportPayload) {
        this.$emit('send', email.toLowerCase(), this.id)

        return
      }

      const payload = {
        email: email.toLowerCase(),
        export_type: 'send_email',
        ...deepCopy(this.exportPayload)
      }

      this.loadingSend = true

      try {
        await this.exportDocument(payload)

        this.showNotification('success', 'Success', 'Successfully sent to email')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingSend = false
      }
    }
  }
}
</script>
<style scoped></style>
